import React from "react";

/**
 * ProgressionIndicator — the signature 7 → 5 → 3 → 2 → 1 mechanic.
 * Variants:
 *   subtle  — hero / inline use, compact horizontal
 *   clear   — shortlist header, medium
 *   strong  — dashboard hero, full-width with labels + current-round pulse
 *
 * `current` controls which stage glows (0–4). Stages before are "advanced",
 * after are "pending".
 */

const STAGES = [
  { n: 7, label: "Shortlist" },
  { n: 5, label: "Values" },
  { n: 3, label: "Chemistry" },
  { n: 2, label: "Finalists" },
  { n: 1, label: "Best heart" },
];

export default function ProgressionIndicator({
  current = 2,
  variant = "clear",
  className = "",
  showCaption = true,
}) {
  if (variant === "subtle") {
    return (
      <div
        data-testid="progression-subtle"
        className={`inline-flex items-center gap-2 glass rounded-full px-3.5 py-1.5 ${className}`}
      >
        {STAGES.map((s, i) => (
          <React.Fragment key={i}>
            <span
              className={`h-5 w-5 rounded-full grid place-items-center text-[10px] font-mono transition-all ${
                i === STAGES.length - 1
                  ? "bg-gradient-gold text-[#05070d]"
                  : i <= current
                    ? "bg-[rgba(212,175,55,0.12)] text-[#F2DE94] ring-1 ring-[rgba(212,175,55,0.35)]"
                    : "bg-white/[0.04] text-white/40 ring-1 ring-white/10"
              }`}
            >
              {s.n}
            </span>
            {i < STAGES.length - 1 && (
              <span
                className={`h-px w-3 ${i < current ? "bg-[rgba(212,175,55,0.4)]" : "bg-white/15"}`}
              />
            )}
          </React.Fragment>
        ))}
        <span className="text-[10px] uppercase tracking-[0.22em] text-white/55 ml-1.5">
          7 → 1
        </span>
      </div>
    );
  }

  const isStrong = variant === "strong";
  const sizeClass = isStrong
    ? "h-14 w-14 md:h-16 md:w-16 text-xl md:text-2xl"
    : "h-11 w-11 md:h-12 md:w-12 text-base md:text-lg";

  return (
    <div
      data-testid={`progression-${variant}`}
      className={`glass-strong rounded-3xl px-5 md:px-8 py-5 md:py-6 relative overflow-hidden ${className}`}
    >
      <div className="absolute inset-x-10 top-[calc(50%-6px)] md:top-[calc(50%-4px)] h-px bg-gradient-to-r from-transparent via-[rgba(212,175,55,0.3)] to-transparent pointer-events-none" />
      <div className="relative grid grid-cols-5 items-center gap-2 md:gap-6">
        {STAGES.map((s, i) => {
          const isCurrent = i === current;
          const isDone = i < current;
          const isWinner = i === STAGES.length - 1;
          const classes = isWinner
            ? "bg-gradient-gold text-[#05070D] glow-gold"
            : isCurrent
              ? "bg-[rgba(212,175,55,0.12)] text-[#F2DE94] ring-2 ring-[rgba(212,175,55,0.55)]"
              : isDone
                ? "bg-[rgba(74,163,106,0.12)] text-[#8FD6A6] ring-1 ring-[rgba(74,163,106,0.45)]"
                : "bg-white/[0.03] text-white/45 ring-1 ring-white/10";
          return (
            <div
              key={i}
              className="flex flex-col items-center gap-2 text-center"
              data-testid={`stage-${s.n}`}
            >
              <div
                className={`relative rounded-full grid place-items-center font-display transition-all duration-500 ${sizeClass} ${classes}`}
              >
                <span>{s.n}</span>
                {(isCurrent || isWinner) && (
                  <span className="absolute inset-0 rounded-full animate-pulse-ring" />
                )}
              </div>
              <div
                className={`text-[10px] md:text-[11px] tracking-[0.18em] uppercase ${
                  isCurrent
                    ? "text-[#F2DE94]"
                    : isWinner
                      ? "text-[#F2DE94]"
                      : isDone
                        ? "text-[#8FD6A6]"
                        : "text-white/45"
                }`}
              >
                {s.label}
              </div>
            </div>
          );
        })}
      </div>
      {showCaption && (
        <div className="mt-4 text-center text-xs md:text-sm text-white/55">
          From seven prospects to{" "}
          <span className="text-[#F2DE94]">one real decision</span> — in five
          refined rounds.
        </div>
      )}
    </div>
  );
}
